// src/utils/validators/textValidator.ts
import * as Yup from "yup";

/** Basic rule set for text fields */
export type TextRule = {
  label?: string;                 // message label, e.g. "Title"
  required?: boolean;             // default: false
  minLen?: number;                // default: undefined
  maxLen?: number;                // default: undefined
  trim?: boolean;                 // default: true
  collapseWhitespace?: boolean;   // default: true (collapse internal spaces)
  pattern?: RegExp;               // must match
  patternMessage?: string;        // custom message for pattern
  disallow?: RegExp;              // must NOT match
  disallowMessage?: string;       // custom message for disallow
};

export type TextSuccess = {
  ok: true;
  value: string;     // normalized
  length: number;
};

export type TextError = {
  ok: false;
  code: "REQUIRED" | "TOO_SHORT" | "TOO_LONG" | "PATTERN" | "DISALLOWED";
  message: string;
  details?: Record<string, any>;
};

export type TextValidateResult = TextSuccess | TextError;

/** Normalize text by options (trim & collapseWhitespace) */
export function normalizeText(
  input: any,
  opts?: Pick<TextRule, "trim" | "collapseWhitespace">
): string {
  let v = (input ?? "").toString();
  const shouldTrim = opts?.trim !== false;                     // default: true
  const collapse = opts?.collapseWhitespace !== false;         // default: true

  if (shouldTrim) v = v.trim();
  if (collapse) {
    // keep newlines; collapse spaces around them
    v = v
      .replace(/\r\n/g, "\n")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n[ \t]+/g, "\n")
      .replace(/[ \t]{2,}/g, " ");
  }
  return v;
}

/** Build a Yup string schema based on a TextRule */
export function buildYupString(rule: TextRule): Yup.StringSchema<string> {
  const label = rule.label || "Field";

  // Start from a string that is ALWAYS a string at runtime and in types:
  // - default("") gives a base value
  // - transform null/undefined to "" then normalize
  // - ensure() coerces undefined/null to "" (and narrows type to string)
  let schema = Yup.string()
    .default("")
    .transform((val) => {
      const base = val == null ? "" : String(val);
      return normalizeText(base, {
        trim: rule.trim !== false,
        collapseWhitespace: rule.collapseWhitespace !== false,
      });
    })
    .ensure(); // <-- guarantees '' instead of undefined/null and narrows type

  // Required check: also guard against "empty after trim"
  if (rule.required) {
    schema = schema
      .required(`${label} is required.`)
      .test("not-empty-after-trim", `${label} is required.`, (val) => {
        const v = normalizeText(val, { trim: rule.trim !== false });
        return v.length > 0;
      });
  }

  // Min/Max length
  if (typeof rule.minLen === "number") {
    schema = schema.min(rule.minLen, `Minimum ${rule.minLen} characters.`);
  }
  if (typeof rule.maxLen === "number") {
    schema = schema.max(rule.maxLen, `Max ${rule.maxLen} characters.`);
  }

  // Pattern (must match)
  if (rule.pattern) {
    schema = schema.test(
      "pattern",
      rule.patternMessage || `${label} format is invalid.`,
      (val) => {
        const v = normalizeText(val, {
          trim: rule.trim !== false,
          collapseWhitespace: rule.collapseWhitespace !== false,
        });
        // If optional and empty, let it pass; otherwise test pattern
        return v.length === 0 ? true : rule.pattern!.test(v);
      }
    );
  }

  // Disallow (must NOT match)
  if (rule.disallow) {
    schema = schema.test(
      "disallow",
      rule.disallowMessage || `${label} contains forbidden characters.`,
      (val) => {
        const v = normalizeText(val, {
          trim: rule.trim !== false,
          collapseWhitespace: rule.collapseWhitespace !== false,
        });
        // Empty passes unless required rule failed above
        return v.length === 0 ? true : !rule.disallow!.test(v);
      }
    );
  }

  // TS now sees this as Yup.StringSchema<string>
  return schema;
}

/** Quickly build a Yup object schema from a field-rules map */
export function buildYupObject(rules: Record<string, TextRule>) {
  const shape: Record<string, Yup.AnySchema> = {};
  for (const key of Object.keys(rules)) {
    shape[key] = buildYupString(rules[key]);
  }
  return Yup.object().shape(shape);
}

/** Runtime (non-Formik) validator with the same rules */
export function validateTextValue(value: any, rule: TextRule): TextValidateResult {
  const v = normalizeText(value, {
    trim: rule.trim !== false,
    collapseWhitespace: rule.collapseWhitespace !== false,
  });
  const len = v.length;

  if (rule.required && len === 0) {
    return { ok: false, code: "REQUIRED", message: `${rule.label || "Field"} is required.` };
  }
  if (typeof rule.minLen === "number" && len < rule.minLen) {
    return { ok: false, code: "TOO_SHORT", message: `Minimum ${rule.minLen} characters.`, details: { length: len } };
  }
  if (typeof rule.maxLen === "number" && len > rule.maxLen) {
    return { ok: false, code: "TOO_LONG", message: `Max ${rule.maxLen} characters.`, details: { length: len } };
  }
  if (rule.pattern && !rule.pattern.test(v)) {
    return { ok: false, code: "PATTERN", message: rule.patternMessage || `${rule.label || "Field"} format is invalid.` };
  }
  if (rule.disallow && rule.disallow.test(v)) {
    return { ok: false, code: "DISALLOWED", message: rule.disallowMessage || `${rule.label || "Field"} contains forbidden characters.` };
  }

  return { ok: true, value: v, length: len };
}
